import moment from 'moment';
import {
  ConsumerError,
  LoginError,
  PasswordError,
  SsoError,
  UserError,
} from './interfaces';

export const getLocalToken = () => localStorage.getItem('token');

export const saveLocalToken = (token: string) =>
  localStorage.setItem('token', token);

export const removeLocalToken = () => localStorage.removeItem('token');

export const formatDate = (date: string) => {
  return date == '1970-01-01T00:00:00'
    ? ''
    : moment(date).format('YYYY-MM-DD HH:mm:ss');
};

export const generatePassword = () =>
  require('generate-password').generate({
    length: 12,
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: false,
    strict: true,
  });

export const loginErrorToMessage = (error: LoginError) => {
  switch (error) {
    case LoginError.CREDENTIALS:
      return 'Incorrect email or password';
    case LoginError.PARTNER_INACTIVE:
      return 'Inactive partner';
    case LoginError.TEMP_PASSWORD_EXPIRED:
      return 'Temporary password expired';
    case LoginError.USER_INACTIVE:
      return 'Inactive user';
  }
};

export const ssoErrorToMessage = (error: SsoError) => {
  switch (error) {
    case SsoError.PROVIDER:
      return 'Authentication error';
    case SsoError.USER_NOT_FOUND:
      return 'User not found';
    case SsoError.USER_INACTIVE:
      return 'Inactive user';
    case SsoError.USER_NOT_SSO:
      return 'SSO authorization is not available to the user';
    case SsoError.PARTNER_INACTIVE:
      return 'Inactive partner';
  }
};

export const userErrorToMessage = (error: UserError) => {
  switch (error) {
    case UserError.ALREADY_EXISTS:
      return 'User with this email already exists';
    case UserError.DOES_NOT_EXIST:
      return 'User does not exist';
  }
};

export const consumerErrorToMessage = (error: ConsumerError) => {
  switch (error) {
    case ConsumerError.ALREADY_EXISTS:
      return 'Mobile user with this email already exists';
    case ConsumerError.DOES_NOT_EXIST:
      return 'Mobile user does not exist';
  }
};

export const passwordErrorToMessage = (error: PasswordError) => {
  switch (error) {
    case PasswordError.MIN_LENGTH:
      return 'Minimum 12 characters';
    case PasswordError.LOW_COMPLEXITY:
      return 'Must contain three of: Uppercase, Lowercase, Number, Special Character';
    case PasswordError.REUSED_LAST_PASSWORDS:
      return 'Cannot be previous five passwords';
    case PasswordError.CONTAINS_USERNAME_OR_EMAIL:
      return 'Cannot contain username or email address';
    case PasswordError.CONTAINS_BANNED_WORDS:
      return 'Your password is too weak. Try adding more characters, numbers, and special symbols.';
    case PasswordError.NOT_UNIQUE:
      return 'Must be unique for each mobile user';
  }
};

export const COUNTRIES = [
  { name: 'Afghanistan', abbr: 'AF' },
  { name: 'Åland Islands', abbr: 'AX' },
  { name: 'Albania', abbr: 'AL' },
  { name: 'Algeria', abbr: 'DZ' },
  { name: 'American Samoa', abbr: 'AS' },
  { name: 'Andorra', abbr: 'AD' },
  { name: 'Angola', abbr: 'AO' },
  { name: 'Antigua and Barbuda', abbr: 'AG' },
  { name: 'Argentina', abbr: 'AR' },
  { name: 'Armenia', abbr: 'AM' },
  { name: 'Aruba', abbr: 'AW' },
  { name: 'Australia', abbr: 'AU' },
  { name: 'Austria', abbr: 'AT' },
  { name: 'Azerbaijan', abbr: 'AZ' },
  { name: 'Bahamas', abbr: 'BS' },
  { name: 'Bahrain', abbr: 'BH' },
  { name: 'Bangladesh', abbr: 'BD' },
  { name: 'Barbados', abbr: 'BB' },
  { name: 'Belarus', abbr: 'BY' },
  { name: 'Belgium', abbr: 'BE' },
  { name: 'Belize', abbr: 'BZ' },
  { name: 'Benin', abbr: 'BJ' },
  { name: 'Bermuda', abbr: 'BM' },
  { name: 'Bhutan', abbr: 'BT' },
  { name: 'Bolivia', abbr: 'BO' },
  { name: 'Bosnia and Herzegovina', abbr: 'BA' },
  { name: 'Botswana', abbr: 'BW' },
  { name: 'Brazil', abbr: 'BR' },
  { name: 'British Indian Ocean Territory', abbr: 'IO' },
  { name: 'Brunei', abbr: 'BN' },
  { name: 'Bulgaria', abbr: 'BG' },
  { name: 'Burkina Faso', abbr: 'BF' },
  { name: 'Burundi', abbr: 'BI' },
  { name: 'Cambodia', abbr: 'KH' },
  { name: 'Cameroon', abbr: 'CM' },
  { name: 'Canada', abbr: 'CA' },
  { name: 'Cape Verde', abbr: 'CV' },
  { name: 'Cayman Islands', abbr: 'KY' },
  { name: 'Central African Republic', abbr: 'CF' },
  { name: 'Chad', abbr: 'TD' },
  { name: 'Chile', abbr: 'CL' },
  { name: 'China', abbr: 'CN' },
  { name: 'Colombia', abbr: 'CO' },
  { name: 'Comoros', abbr: 'KM' },
  { name: 'Congo', abbr: 'CG' },
  { name: 'Congo, The Democratic Republic of the', abbr: 'CD' },
  { name: 'Costa Rica', abbr: 'CR' },
  { name: "Cote D'Ivoire", abbr: 'CI' },
  { name: 'Croatia', abbr: 'HR' },
  { name: 'Cuba', abbr: 'CU' },
  { name: 'Cyprus', abbr: 'CY' },
  { name: 'Czech Republic', abbr: 'CZ' },
  { name: 'Denmark', abbr: 'DK' },
  { name: 'Djibouti', abbr: 'DJ' },
  { name: 'Dominica', abbr: 'DM' },
  { name: 'Dominican Republic', abbr: 'DO' },
  { name: 'Ecuador', abbr: 'EC' },
  { name: 'Egypt', abbr: 'EG' },
  { name: 'El Salvador', abbr: 'SV' },
  { name: 'Equatorial Guinea', abbr: 'GQ' },
  { name: 'Eritrea', abbr: 'ER' },
  { name: 'Estonia', abbr: 'EE' },
  { name: 'Ethiopia', abbr: 'ET' },
  { name: 'Faroe Islands', abbr: 'FO' },
  { name: 'Fiji', abbr: 'FJ' },
  { name: 'Finland', abbr: 'FI' },
  { name: 'France', abbr: 'FR' },
  { name: 'French Guiana', abbr: 'GF' },
  { name: 'French Polynesia', abbr: 'PF' },
  { name: 'Gabon', abbr: 'GA' },
  { name: 'Gambia', abbr: 'GM' },
  { name: 'Georgia', abbr: 'GE' },
  { name: 'Germany', abbr: 'DE' },
  { name: 'Ghana', abbr: 'GH' },
  { name: 'Gibraltar', abbr: 'GI' },
  { name: 'Greece', abbr: 'GR' },
  { name: 'Greenland', abbr: 'GL' },
  { name: 'Grenada', abbr: 'GD' },
  { name: 'Guadeloupe', abbr: 'GP' },
  { name: 'Guam', abbr: 'GU' },
  { name: 'Guatemala', abbr: 'GT' },
  { name: 'Guernsey', abbr: 'GG' },
  { name: 'Guinea', abbr: 'GN' },
  { name: 'Guinea-Bissau', abbr: 'GW' },
  { name: 'Guyana', abbr: 'GY' },
  { name: 'Haiti', abbr: 'HT' },
  { name: 'Honduras', abbr: 'HN' },
  { name: 'Hong Kong', abbr: 'HK' },
  { name: 'Hungary', abbr: 'HU' },
  { name: 'Iceland', abbr: 'IS' },
  { name: 'India', abbr: 'IN' },
  { name: 'Indonesia', abbr: 'ID' },
  { name: 'Iran, Islamic Republic Of', abbr: 'IR' },
  { name: 'Iraq', abbr: 'IQ' },
  { name: 'Ireland', abbr: 'IE' },
  { name: 'Isle of Man', abbr: 'IM' },
  { name: 'Israel', abbr: 'IL' },
  { name: 'Italy', abbr: 'IT' },
  { name: 'Jamaica', abbr: 'JM' },
  { name: 'Japan', abbr: 'JP' },
  { name: 'Jersey', abbr: 'JE' },
  { name: 'Jordan', abbr: 'JO' },
  { name: 'Kazakhstan', abbr: 'KZ' },
  { name: 'Kenya', abbr: 'KE' },
  { name: 'Kiribati', abbr: 'KI' },
  { name: "Korea, Democratic People's Republic of", abbr: 'KP' },
  { name: 'Korea, Republic of', abbr: 'KR' },
  { name: 'Kuwait', abbr: 'KW' },
  { name: 'Kyrgyzstan', abbr: 'KG' },
  { name: 'Laos', abbr: 'LA' },
  { name: 'Latvia', abbr: 'LV' },
  { name: 'Lebanon', abbr: 'LB' },
  { name: 'Lesotho', abbr: 'LS' },
  { name: 'Liberia', abbr: 'LR' },
  { name: 'Libya', abbr: 'LY' },
  { name: 'Liechtenstein', abbr: 'LI' },
  { name: 'Lithuania', abbr: 'LT' },
  { name: 'Luxembourg', abbr: 'LU' },
  { name: 'Macao', abbr: 'MO' },
  { name: 'Macedonia, The Former Yugoslav Republic of', abbr: 'MK' },
  { name: 'Madagascar', abbr: 'MG' },
  { name: 'Malawi', abbr: 'MW' },
  { name: 'Malaysia', abbr: 'MY' },
  { name: 'Maldives', abbr: 'MV' },
  { name: 'Mali', abbr: 'ML' },
  { name: 'Malta', abbr: 'MT' },
  { name: 'Marshall Islands', abbr: 'MH' },
  { name: 'Martinique', abbr: 'MQ' },
  { name: 'Mauritania', abbr: 'MR' },
  { name: 'Mauritius', abbr: 'MU' },
  { name: 'Mayotte', abbr: 'YT' },
  { name: 'Mexico', abbr: 'MX' },
  { name: 'Micronesia, Federated States of', abbr: 'FM' },
  { name: 'Moldova, Republic of', abbr: 'MD' },
  { name: 'Monaco', abbr: 'MC' },
  { name: 'Mongolia', abbr: 'MN' },
  { name: 'Morocco', abbr: 'MA' },
  { name: 'Mozambique', abbr: 'MZ' },
  { name: 'Myanmar', abbr: 'MM' },
  { name: 'Namibia', abbr: 'NA' },
  { name: 'Nepal', abbr: 'NP' },
  { name: 'Netherlands', abbr: 'NL' },
  { name: 'New Caledonia', abbr: 'NC' },
  { name: 'New Zealand', abbr: 'NZ' },
  { name: 'Nicaragua', abbr: 'NI' },
  { name: 'Niger', abbr: 'NE' },
  { name: 'Nigeria', abbr: 'NG' },
  { name: 'Northern Mariana Islands', abbr: 'MP' },
  { name: 'Norway', abbr: 'NO' },
  { name: 'Oman', abbr: 'OM' },
  { name: 'Pakistan', abbr: 'PK' },
  { name: 'Palau', abbr: 'PW' },
  { name: 'Palestine', abbr: 'PS' },
  { name: 'Panama', abbr: 'PA' },
  { name: 'Papua New Guinea', abbr: 'PG' },
  { name: 'Paraguay', abbr: 'PY' },
  { name: 'Peru', abbr: 'PE' },
  { name: 'Philippines', abbr: 'PH' },
  { name: 'Poland', abbr: 'PL' },
  { name: 'Portugal', abbr: 'PT' },
  { name: 'Puerto Rico', abbr: 'PR' },
  { name: 'Qatar', abbr: 'QA' },
  { name: 'Reunion', abbr: 'RE' },
  { name: 'Romania', abbr: 'RO' },
  { name: 'Russian', abbr: 'RU' },
  { name: 'Rwanda', abbr: 'RW' },
  { name: 'Saint Kitts and Nevis', abbr: 'KN' },
  { name: 'Saint Lucia', abbr: 'LC' },
  { name: 'Saint Vincent and the Grenadines', abbr: 'VC' },
  { name: 'Samoa', abbr: 'WS' },
  { name: 'San Marino', abbr: 'SM' },
  { name: 'Sao Tome and Principe', abbr: 'ST' },
  { name: 'Saudi Arabia', abbr: 'SA' },
  { name: 'Senegal', abbr: 'SN' },
  { name: 'Serbia and Montenegro', abbr: 'CS' },
  { name: 'Seychelles', abbr: 'SC' },
  { name: 'Sierra Leone', abbr: 'SL' },
  { name: 'Singapore', abbr: 'SG' },
  { name: 'Slovakia', abbr: 'SK' },
  { name: 'Slovenia', abbr: 'SI' },
  { name: 'Solomon Islands', abbr: 'SB' },
  { name: 'Somalia', abbr: 'SO' },
  { name: 'South Africa', abbr: 'ZA' },
  { name: 'Spain', abbr: 'ES' },
  { name: 'Sri Lanka', abbr: 'LK' },
  { name: 'Sudan', abbr: 'SD' },
  { name: 'Suriname', abbr: 'SR' },
  { name: 'Swaziland', abbr: 'SZ' },
  { name: 'Sweden', abbr: 'SE' },
  { name: 'Switzerland', abbr: 'CH' },
  { name: 'Syria', abbr: 'SY' },
  { name: 'Taiwan', abbr: 'TW' },
  { name: 'Tajikistan', abbr: 'TJ' },
  { name: 'Tanzania, United Republic of', abbr: 'TZ' },
  { name: 'Thailand', abbr: 'TH' },
  { name: 'Timor-Leste', abbr: 'TL' },
  { name: 'Togo', abbr: 'TG' },
  { name: 'Tonga', abbr: 'TO' },
  { name: 'Trinidad and Tobago', abbr: 'TT' },
  { name: 'Tunisia', abbr: 'TN' },
  { name: 'Turkey', abbr: 'TR' },
  { name: 'Turkmenistan', abbr: 'TM' },
  { name: 'Turks and Caicos Islands', abbr: 'TC' },
  { name: 'Tuvalu', abbr: 'TV' },
  { name: 'Uganda', abbr: 'UG' },
  { name: 'Ukraine', abbr: 'UA' },
  { name: 'United Arab Emirates', abbr: 'AE' },
  { name: 'United Kingdom', abbr: 'GB' },
  { name: 'United States', abbr: 'US' },
  { name: 'U.S. Outlying Islands', abbr: 'UM' },
  { name: 'U.S. Virgin Islands', abbr: 'VI' },
  { name: 'Uruguay', abbr: 'UY' },
  { name: 'Uzbekistan', abbr: 'UZ' },
  { name: 'Vanuatu', abbr: 'VU' },
  { name: 'Venezuela', abbr: 'VE' },
  { name: 'Vietnam', abbr: 'VN' },
  { name: 'Western Sahara', abbr: 'EH' },
  { name: 'Yemen', abbr: 'YE' },
  { name: 'Zambia', abbr: 'ZM' },
  { name: 'Zimbabwe', abbr: 'ZW' },
];
